import { useState, useCallback } from 'react';
import useFetchWithMsal from '../useFetchWithMsal';
import { protectedResources } from '../../authConfig';
import { useSnackbar } from 'notistack';


export const useLineMapping = () => {
  const [loading, setLoading] = useState(false);
  const [lineMappingCheck, setLineMappingCheck] = useState(null);
  const [error, setError] = useState(null);
  
  const { enqueueSnackbar } = useSnackbar();
  
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });


  const checkLineMappingRequired = useCallback(async (approvalPackageId) => {
    if (!approvalPackageId) {
      setError('מזהה חבילת האישור חסר');
      return null;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await execute(
        'GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/line-mapping/${approvalPackageId}/check-line-mapping`
      );

      if (response?.status === 'SUCCESS') {
        setLineMappingCheck(response.data);
        
        if (response.data.requiresLineMapping) {
          if (response.data.goodsReceiptPriceChanges?.hasPriceChanges) {
            enqueueSnackbar(
              'זוהו שינויי מחירים בקבלת הסחורה - יש להשתמש במחירים המעודכנים', 
              { variant: 'info', persist: true }
            );
          } else {
            enqueueSnackbar(
              'זוהו הפרשי מחירים משמעותיים - נדרש שיוך ידני של שורות', 
              { variant: 'warning', persist: true }
            );
          }
        } else {
          enqueueSnackbar(
            'הפרשי המחירים קטנים - ניתן להמשיך עם השוואה רגילה', 
            { variant: 'success' }
          );
        }
        
        return response.data;
      } else {
        throw new Error(response?.message || 'שגיאה בבדיקת הצורך בשיוך שורות');
      }
    } catch (error) {
      console.error('Error checking line mapping requirement:', error);
      const errorMessage = error.message || 'שגיאה בבדיקת הצורך בשיוך שורות';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [execute, enqueueSnackbar]);


  const saveLineMapping = useCallback(async (approvalPackageId, lineMapping) => {
    if (!approvalPackageId || !lineMapping) {
      const errorMessage = 'מזהה חבילת האישור או שיוך השורות חסרים';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await execute(
        'POST',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/line-mapping/${approvalPackageId}/line-mapping`,
        { lineMapping }
      );

      if (response?.status === 'SUCCESS') {
        enqueueSnackbar('שיוך השורות נשמר בהצלחה', { variant: 'success' });
        return true;
      } else {
        throw new Error(response?.message || 'שגיאה בשמירת השיוך');
      }
    } catch (error) {
      console.error('Error saving line mapping:', error);
      const errorMessage = error.message || 'שגיאה בשמירת השיוך';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    } finally {
      setLoading(false);
    }
  }, [execute, enqueueSnackbar]);


  const getExistingLineMapping = useCallback(async (approvalPackageId) => {
    if (!approvalPackageId) {
      setError('מזהה חבילת האישור חסר');
      return null;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await execute(
        'GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/line-mapping/${approvalPackageId}/line-mapping`
      );

      if (response?.status === 'SUCCESS') {
        return response.data;
      } else {
        throw new Error(response?.message || 'שגיאה בקבלת השיוך הקיים');
      }
    } catch (error) {
      console.error('Error getting existing line mapping:', error);
      const errorMessage = error.message || 'שגיאה בקבלת השיוך הקיים';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [execute]);

 
  const getAiSuggestedMapping = useCallback(async (approvalPackageId) => {
    if (!approvalPackageId) {
      setError('מזהה חבילת האישור חסר');
      return null;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await execute(
        'GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/line-mapping/${approvalPackageId}/ai-suggested-mapping`
      );

      if (response?.status === 'SUCCESS') {
        return response.data;
      } else {
        throw new Error(response?.message || 'שגיאה בטעינת הצעת השיוך');
      }
    } catch (error) {
      console.error('Error getting AI suggestion:', error);
      const errorMessage = error.message || 'שגיאה בטעינת הצעת השיוך';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [execute]);


  const approveAiMapping = useCallback(async (approvalPackageId, aiMappings) => {
    if (!approvalPackageId || !aiMappings) {
      const errorMessage = 'מזהה חבילת האישור או השיוכים חסרים';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return { success: false };
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await execute(
        'POST',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/line-mapping/${approvalPackageId}/approve-ai-mapping`,
        { lineMapping: aiMappings, useAiSuggestion: true }
      );

      if (response?.status === 'SUCCESS') {
        enqueueSnackbar('הצעת השיוך אושרה בהצלחה', { variant: 'success' });
        return { 
          success: true, 
          invoiceStatus: response.data?.invoiceStatus || 'PENDING_APPROVAL' 
        };
      } else {
        throw new Error(response?.message || 'שגיאה באישור השיוך');
      }
    } catch (error) {
      console.error('Error approving AI mapping:', error);
      const errorMessage = error.message || 'שגיאה באישור השיוך';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [execute, enqueueSnackbar]);


  const clearData = useCallback(() => {
    setLineMappingCheck(null);
    setError(null);
  }, []);

  return {
    loading,
    lineMappingCheck,
    error,
    checkLineMappingRequired,
    saveLineMapping,
    getExistingLineMapping,
    getAiSuggestedMapping,
    approveAiMapping,
    clearData
  };
};